import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../store/authStore';
import { Card } from './ui/card';
import { Shield, Users, QrCode, Link2, LogIn, Settings2, Clock, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CAT = {
  auth:        { icon: LogIn,    color: 'bg-violet-50 text-violet-600' },
  team:        { icon: Users,    color: 'bg-blue-50 text-blue-600' },
  role:        { icon: Shield,   color: 'bg-amber-50 text-amber-600' },
  qr:          { icon: QrCode,   color: 'bg-emerald-50 text-emerald-600' },
  integration: { icon: Link2,    color: 'bg-cyan-50 text-cyan-600' },
};
const fallback = { icon: Clock, color: 'bg-zinc-50 text-zinc-600' };

const timeAgo = (ts) => {
  const d = Math.floor((Date.now() - new Date(ts).getTime()) / 1000);
  if (d < 60) return 'just now';
  if (d < 3600) return `${Math.floor(d / 60)}m ago`;
  if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
  return `${Math.floor(d / 86400)}d ago`;
};

export const RecentAuditWidget = ({ wsEvents = [] }) => {
  const { token } = useAuth();
  const [entries, setEntries] = useState([]);

  // Fetch latest 5 on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/audit-log`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { page_size: 5 },
        });
        setEntries(res.data.entries || []);
      } catch {}
    })();
  }, [token]);

  // Prepend real-time audit events
  useEffect(() => {
    if (!wsEvents.length) return;
    const last = wsEvents[0];
    if (last?.type === 'audit_event' && last.entry) {
      setEntries(prev => [last.entry, ...prev].slice(0, 5));
    }
  }, [wsEvents]);

  return (
    <Card className="p-5 border border-[#E2E8F0]" data-testid="recent-audit-widget">
      <div className="flex items-center justify-between mb-3">
        <h3 className="font-bold text-sm" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Team Activity
        </h3>
        <Link to="/audit-log" className="text-xs text-[#1d4ed8] hover:underline flex items-center gap-0.5" data-testid="audit-widget-view-all">
          View all <ArrowRight size={11} />
        </Link>
      </div>

      {entries.length === 0 ? (
        <p className="text-xs text-[#94A3B8] text-center py-4" data-testid="audit-widget-empty">No recent team activity</p>
      ) : (
        <div className="space-y-2.5" data-testid="audit-widget-list">
          {entries.map((e, i) => {
            const meta = CAT[e.category] || fallback;
            const Icon = meta.icon;
            return (
              <div key={`${e.timestamp}-${i}`} className="flex gap-2.5" data-testid={`audit-widget-item-${i}`}>
                <div className={`w-6 h-6 rounded-md flex items-center justify-center shrink-0 mt-0.5 ${meta.color}`}>
                  <Icon size={12} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-xs text-[#1E293B] leading-relaxed truncate">
                    <span className="font-semibold">{e.user_name}</span>{' '}
                    {e.detail || e.action}
                  </p>
                  <p className="text-[10px] text-[#94A3B8]">{timeAgo(e.timestamp)}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </Card>
  );
};
